<template>
  <property-report-card
    title="Unit-Level Insights"
    description="Explore competitor pricing and concessions at the individual unit level."
    :printing-mode="printingMode"
    :hide-header="!printingMode"
    transparent-card
  >
    <div class="row q-col-gutter-sm q-mt-md">
      <div
        v-for="ins in computeUnitMetric"
        :key="ins.label"
        class="col-12 col-xs-6 col-sm-4 col-md-3 col-lg-2"
      >
        <q-card flat bordered>
          <q-card-section>
            <div class="flex row title">
              <div>{{ ins.label }}</div>
            </div>
          </q-card-section>
          <q-separator />
          <q-markup-table dense flat class="q-mt-sm" separator="none">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col" class="text-center text-grey-7 no-padding">Available</th>
                <th scope="col" class="text-center text-grey-7 no-padding">Leased</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Subject</td>
                <td class="text-center no-padding">
                  <q-badge class="bg-deep-purple-light" text-color="primary" dense square>
                    {{ ins.formattedValues.subjectActive }}
                  </q-badge>
                </td>
                <td class="text-center no-padding">
                  <q-badge class="bg-deep-purple-light" text-color="primary" dense square>
                    {{ ins.formattedValues.subjectClosed }}
                  </q-badge>
                </td>
              </tr>
              <tr>
                <td>Comps</td>
                <td class="text-center no-padding">
                  <q-badge dense square outline color="accent">
                    {{ ins.formattedValues.compsActive }}
                  </q-badge>
                </td>
                <td class="text-center no-padding">
                  <q-badge dense square outline color="accent">
                    {{ ins.formattedValues.compsClosed }}
                  </q-badge>
                </td>
              </tr>
              <tr class="text-bold">
                <td>All</td>
                <td class="text-center no-padding">
                  {{ ins.formattedValues.active }}
                </td>
                <td class="text-center no-padding">
                  {{ ins.formattedValues.closed }}
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card>
      </div>
    </div>
  </property-report-card>
</template>

<script lang="ts">
import type { Availability, PropertyDetailsResponse, AvailabilityPeriod, Price } from 'common'
import { getUnitsByFilter, daysOnMarket, getEffPsf, getPsf } from 'common'
import type { PropType } from 'vue'
import { formatMoney, formatNumber, avg, sum } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

type Insight = {
  label: string
  description: string
  formattedValues: {
    closed: string
    subjectClosed: string
    compsClosed: string
    active: string
    subjectActive: string
    compsActive: string
  }
}

export default {
  name: 'PropertyCompareUnitMetrics',
  components: { PropertyReportCard },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formatMoney,
      formatNumber,
      metrics: [
        {
          label: 'Listings',
          description: 'Number of units of this type at your property',
          getter: () => [1],
          agg: sum,
          formatter: (val: number | null) => (val ? formatNumber(val) : '−')
        },
        {
          label: 'Days on Market',
          description: 'Average number of days a unit is on the market before being leased',
          getter: ({ period }) => [daysOnMarket(period, this.dateRange.to)],
          agg: avg,
          formatter: (val: number | null) => (val ? formatNumber(val) : '−')
        },
        {
          label: 'Asking Rent',
          description: 'Average rent for a unit, before applying detected concessions',
          getter: ({ lastHistory }) => [lastHistory.price ?? lastHistory.min_price],
          agg: avg,
          formatter: (val: number | null) => (val ? formatMoney(val, 0) : '−')
        },
        {
          label: 'Asking Rent PSF',
          description:
            'Average asking rent per square foot for a unit, before applying detected concessions',
          getter: ({ unit, lastHistory }) => [
            getPsf({
              price: lastHistory.price,
              min_price: lastHistory.min_price,
              min_sqft: unit.min_sqft,
              sqft: unit.sqft,
              max_sqft: unit.max_sqft
            })
          ],
          agg: avg,
          formatter: (val: number | null) => (val ? formatMoney(val, 2) : '−')
        },
        {
          label: 'Effective Rent',
          description: 'Average effective rent for a unit, after applying detected concessions',
          getter: ({ lastHistory }) => [
            lastHistory.effective_price || lastHistory.min_effective_price
          ],
          agg: avg,
          formatter: (val: number | null) => (val ? formatMoney(val, 0) : '−')
        },
        {
          label: 'Effective Rent PSF',
          description:
            'Average effective rent per square foot for a unit, after applying detected concessions',
          getter: ({ unit, lastHistory }) => [
            getEffPsf({
              effective_price: lastHistory.effective_price,
              min_effective_price: lastHistory.min_effective_price,
              min_sqft: unit.min_sqft,
              sqft: unit.sqft,
              max_sqft: unit.max_sqft
            })
          ],
          agg: avg,
          formatter: (val: number | null) => (val ? formatMoney(val, 2) : '−')
        }
      ] as {
        label: string
        description: string
        getter: (val: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }) => number[]
        agg: (vals: (number | null)[]) => number | null
        formatter: (val: number | null) => string
      }[]
    }
  },
  computed: {
    computeUnitMetric() {
      const metricValues: Record<
        string,
        {
          closed: (null | number)[]
          subjectClosed: (null | number)[]
          compsClosed: (null | number)[]
          active: (null | number)[]
          subjectActive: (null | number)[]
          compsActive: (null | number)[]
        }
      > = {}

      // init the objects such that we don't have to check later in the loop
      for (const metric of this.metrics) {
        metricValues[metric.label] = {
          closed: [],
          subjectClosed: [],
          compsClosed: [],
          active: [],
          subjectActive: [],
          compsActive: []
        }
      }

      for (const { prop, units } of this.filteredProperties) {
        const closed = getUnitsByFilter(prop, units, this.dateRange.to, 'closed')
        const active = getUnitsByFilter(prop, units, this.dateRange.to, 'active')
        for (const metric of this.metrics) {
          for (const { unit, lastHistory, period } of closed) {
            const val = metric.getter({ unit, lastHistory, period })
            metricValues[metric.label].closed.push(...val)
            if (prop.id === this.subjectDetail?.id) {
              metricValues[metric.label].subjectClosed.push(...val)
            } else {
              metricValues[metric.label].compsClosed.push(...val)
            }
          }
          for (const { unit, lastHistory, period } of active) {
            const val = metric.getter({ unit, lastHistory, period })
            metricValues[metric.label].active.push(...val)
            if (prop.id === this.subjectDetail?.id) {
              metricValues[metric.label].subjectActive.push(...val)
            } else {
              metricValues[metric.label].compsActive.push(...val)
            }
          }
        }
      }

      return this.metrics.map((metric) => {
        return {
          label: metric.label,
          description: metric.description,
          formattedValues: {
            closed: metric.formatter(metric.agg(metricValues[metric.label].closed)),
            subjectClosed: metric.formatter(metric.agg(metricValues[metric.label].subjectClosed)),
            compsClosed: metric.formatter(metric.agg(metricValues[metric.label].compsClosed)),
            active: metric.formatter(metric.agg(metricValues[metric.label].active)),
            subjectActive: metric.formatter(metric.agg(metricValues[metric.label].subjectActive)),
            compsActive: metric.formatter(metric.agg(metricValues[metric.label].compsActive))
          }
        } as Insight
      })
    }
  }
}
</script>

<style scoped>
.title {
  font-weight: 500;
}
</style>
